import { api } from "@shared/lib/api";
import { BackednResponse } from "@shared/lib/types";

import {
  IGetReports,
  IReportCustomFieledConfig,
  IReportDetail,
  IValidateReportCsvResponse,
  ReportMedia,
} from "@shared/lib/types/reports";

export class ReportService {
  public static async exportReport(reportId: string) {
    const url = `download/reportMediasV2`;
    const response = (await api
      .post(url, { json: { reportId } })
      .json()) as BackednResponse<{ url: string }>;
    return response;
  }
  public static async getReports(
    searchQuery?: string,
    limit?: number,
    pageNo?: number
  ): Promise<BackednResponse<IGetReports>> {
    const params = new URLSearchParams();
    if (searchQuery) {
      params.append("searchQuery", searchQuery);
    }
    if (limit) {
      params.append("limit", limit.toString());
    }
    if (pageNo) {
      params.append("pageNo", pageNo.toString());
    }
    const url = `report/${params.toString() ? `?${params.toString()}` : ""}`;
    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<IGetReports>;
    return downloads;
  }

  public static async getReportDetail(
    id: string,
    isNonTrackable: boolean = false,
    facets?: {
      [key: string]: string[];
    },
    limit?: number,
    pageNo?: number
  ) {
    const params = new URLSearchParams();

    for (const key in facets) {
      if (facets[key] && facets[key].length > 0) {
        facets[key].forEach((value) => {
          params.append(key, value);
        });
      }
    }
    if (limit) {
      params.append("limit", limit.toString());
    }
    if (pageNo) {
      params.append("pageNo", pageNo.toString());
    }
    params.append("isNonTrackable", isNonTrackable.toString());
    const url = `report/${id}`;
    const finalUrl = params.toString() ? `${url}?${params.toString()}` : url;

    const report = (await api
      .get(finalUrl)
      .json()) as BackednResponse<IReportDetail>;
    return report;
  }

  public static async updateReportMediaCost(
    reportMediaId: string,
    cost: number
  ) {
    const url = `report/media/edit-cost`;
    const response = (await api
      .post(url, { json: { reportMediaId, cost } })
      .json()) as BackednResponse<ReportMedia>;
    return response;
  }
  public static async getFacets(id: string) {
    const url = `report/${id}/facets`;
    const report = (await api.get(url).json()) as BackednResponse<{
      [key: string]: { _id: string }[];
    }>;
    return report;
  }

  public static async createReport({
    name,
    updateForIntervalHourFrequency,
    updateTillDate,
  }: {
    name: string;
    updateForIntervalHourFrequency: number;
    updateTillDate: string;
  }) {
    const url = `report`;
    return await api
      .post(url, {
        json: { name, updateForIntervalHourFrequency, updateTillDate },
      })
      .json();
  }
  public static async addMediaReport({
    link,
    reportId,
  }: {
    link: string;
    reportId: string;
  }) {
    const url = `report/add-media/link`;
    return await api.post(url, { json: { link, reportId } }).json();
  }

  public static async validateReportCsv(csvLink: string) {
    const url = `report/validate-media/link`;
    const response = (await api
      .post(url, { json: { validateBy: { csv: csvLink } } })
      .json()) as BackednResponse<IValidateReportCsvResponse[]>;
    return response;
  }

  public static async addMediaLinksToReport(
    reportId: string,
    reportingMedialinks: string[]
  ) {
    const url = `bulk-action/add-media-links/reporting`;
    const response = (await api
      .post(url, { json: { reportId, reportingMedialinks } })
      .json()) as BackednResponse<object>;
    return response;
  }

  public static async getReportMedia(
    mediaId: string
  ): Promise<BackednResponse<ReportMedia>> {
    const url = `report/media/${mediaId}`;
    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<ReportMedia>;
    return downloads;
  }

  public static async deleteMediaReport({ mediaId }: { mediaId: string }) {
    const url = `report/media/${mediaId}`;
    return await api.delete(url).json();
  }

  public static async createReportDataFields(
    reportId: string,
    customFieldsV2: IReportCustomFieledConfig
  ) {
    const url = `report/custom-fields`;
    await api.post(url, {
      json: {
        reportId,
        customFieldsV2,
      },
    });
  }

  public static async editReportDataFieldV2Data(
    reportingMediaId: string,
    dataField: { [key: string]: string | number }
  ) {
    const url = `report/custom-fields/media/dynamic-fields`;
    await api.post(url, {
      json: {
        reportingMediaId,
        dynamicFields: { ...dataField },
      },
    });
  }
  public static async toggleReportDataFieldV2(
    reportId: string,
    visibileCustomFieldsV2: IReportCustomFieledConfig[]
  ) {
    const url = `report/custom-fields/visible`;
    await api.post(url, {
      json: {
        reportId,
        visibileCustomFieldsV2,
      },
    });
  }
}
